//prettier-ignore
import "react-native-reanimated";

import { queryClient } from "@/src/config/react-query";
import { toastConfig } from "@/src/config/toast.config";
import { AuthProvider } from "@/src/contexts/auth.context";
import i18n, { initI18n } from "@/src/i18n";
import { ThemeProvider, useTheme } from "@/src/styles/theme-provider";
import { QueryClientProvider } from "@tanstack/react-query";
import { Slot } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import { ActivityIndicator, View } from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import {
  MD3DarkTheme,
  MD3LightTheme,
  PaperProvider,
} from "react-native-paper";
import { SafeAreaProvider, SafeAreaView } from "react-native-safe-area-context";
import Toast from "react-native-toast-message";
import "../global.css";

/**
 * Themed shell: fills the safe-area insets with the active theme background so
 * the notch/home-indicator areas never flash an unthemed (white) background,
 * and adapts the status-bar content to the resolved color scheme.
 *
 * Color values come from color-theme.ts via `themeVars` (the canonical theme
 * source). react-native-paper still provides a handful of components
 * (DataTable, RadioButton, Snackbar, …) so PaperProvider is fed a stock MD3
 * theme whose primary/background are synced to the active scheme.
 */
function RootShell() {
  const { resolvedScheme, themeVars } = useTheme();
  const backgroundColor = themeVars["--color-chalk-default"] as string;
  const md3Base = resolvedScheme === "dark" ? MD3DarkTheme : MD3LightTheme;
  const paperTheme = {
    ...md3Base,
    colors: {
      ...md3Base.colors,
      primary: themeVars["--color-primary-default"] as string,
      background: backgroundColor,
      surface: backgroundColor,
    },
  };

  return (
    <GestureHandlerRootView style={{ flex: 1, backgroundColor }}>
      <StatusBar style={resolvedScheme === "dark" ? "light" : "dark"} />
      <SafeAreaView style={{ flex: 1, backgroundColor }}>
        <PaperProvider theme={paperTheme}>
          <I18nextProvider i18n={i18n}>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <Slot />
                <Toast config={toastConfig} />
              </AuthProvider>
            </QueryClientProvider>
          </I18nextProvider>
        </PaperProvider>
      </SafeAreaView>
    </GestureHandlerRootView>
  );
}

export default function RootLayout() {
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    initI18n().then(() => setI18nReady(true));
  }, []);

  if (!i18nReady) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <SafeAreaProvider>
      <ThemeProvider>
        <RootShell />
      </ThemeProvider>
    </SafeAreaProvider>
  );
}
